import React from 'react';
import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { CaretDown, CaretUp, CheckCircle, XCircle } from '@phosphor-icons/react';
import { AlignedSkill, ConcernOrGap, GapsOrMisalignment, Strength } from '@/types/AnalysisResult';
import { Collapsible } from '@chakra-ui/react';

type RequirementMatchItem = AlignedSkill | GapsOrMisalignment | Strength | ConcernOrGap;

type RequirementMatchType = 'aligned' | 'misaligned';

const AlignedItemIcon = ({ size = 24 }: { size?: number }) => {
  return (
    <Box w={`${size}px`} h={`${size}px`}>
      <CheckCircle size={size} color="#0fa958" weight="fill" />
    </Box>
  );
};

const MisalignedItemIcon = ({ size = 24 }: { size?: number }) => {
  return (
    <Box w={`${size}px`} h={`${size}px`}>
      <XCircle size={size} color="#ff8c40" weight="fill" />
    </Box>
  );
};

export default function RequirementMatchCollapsible({
  matchType,
  title,
  items,
  isOpen,
  setOpen
}: {
  matchType: RequirementMatchType;
  title: string;
  items: Array<RequirementMatchItem>;
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const getIcon = (isMobile: boolean = false) => {
    const size = isMobile ? 20 : 24;
    return matchType == 'aligned' ? (
      <AlignedItemIcon size={size} />
    ) : (
      <MisalignedItemIcon size={size} />
    );
  };

  return (
    <Box
      border="1px solid"
      borderColor="neutrals.200"
      borderRadius="8px"
      p={0}
      mb={{ base: '16px', md: '24px' }}
      w="100%"
      h="100%"
    >
      <Collapsible.Root open={isOpen} onOpenChange={({ open }) => setOpen(open)}>
        <Collapsible.Trigger
          w="100%"
          h="100%"
          px={{ base: '16px', md: '24px' }}
          py={{ base: '12px', md: '16px' }}
          bg="neutrals.50"
          cursor="pointer"
          borderBottom={isOpen ? '1px solid' : '0px'}
          borderColor="neutrals.200"
          borderTopRadius="8px"
          borderBottomRadius={isOpen ? '0px' : '8px'}
        >
          <Flex justifyContent="space-between" alignItems="center" bg="neutrals.50">
            <Flex alignItems="center" w="100%" h="100%">
              {getIcon()}
              <Box
                color="neutrals.700"
                fontSize={{ base: '16px', md: '18px' }}
                fontWeight="600"
                ml={{ base: '6px', md: '8px' }}
              >
                {title}
              </Box>
            </Flex>
            {isOpen ? (
              <CaretUp size={32} color="#242424" />
            ) : (
              <CaretDown size={32} color="#242424" />
            )}
          </Flex>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Stack gap="0px" my="10px">
            {items && items.length > 0 ? (
              items.map((item, index) => (
                <React.Fragment key={index}>
                  <Flex
                    alignItems="flex-start"
                    w="100%"
                    h="100%"
                    px={{ base: '16px', md: '24px' }}
                    py={{ base: '8px', md: '12px' }}
                    gap={{ base: '8px', md: '24px' }}
                  >
                    <Box mt="2px">{getIcon(true)}</Box>
                    <Box
                      color="neutrals.700"
                      fontSize={{ base: '14px', md: '16px' }}
                      fontWeight="bold"
                      flex="1"
                    >
                      {item.title}
                      {'explanation' in item && (
                        <Text
                          color="neutrals.500"
                          fontSize={{ base: '12px', md: '14px' }}
                          fontWeight="normal"
                          mt="4px"
                          lineHeight="1.4"
                        >
                          {item.explanation}
                        </Text>
                      )}
                      {'alignment' in item && (
                        <Text
                          color="neutrals.500"
                          fontSize={{ base: '12px', md: '14px' }}
                          fontWeight="normal"
                          mt="4px"
                          lineHeight="1.4"
                        >
                          {item.alignment}
                        </Text>
                      )}
                      {'description' in item && (
                        <Text
                          color="neutrals.500"
                          fontSize={{ base: '12px', md: '14px' }}
                          fontWeight="normal"
                          mt="4px"
                          lineHeight="1.4"
                        >
                          {item.description}
                        </Text>
                      )}
                    </Box>
                  </Flex>
                  {index < items.length - 1 && (
                    <Box
                      as="hr"
                      width="100%"
                      border="none"
                      borderTop="1px solid"
                      borderColor="neutrals.200"
                      my={{ base: '6px', md: '8px' }}
                    />
                  )}
                </React.Fragment>
              ))
            ) : (
              <Text
                color="neutrals.500"
                fontSize={{ base: '12px', md: '14px' }}
                px={{ base: '16px', md: '24px' }}
                py={{ base: '8px', md: '12px' }}
              >
                No {matchType} items
              </Text>
            )}
          </Stack>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}
